<script setup>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { DxList } from 'devextreme-vue/list'

import { planningApi } from '@/apis/planning.api'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useODataSource } from '@/composables/data-grid'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'
import { useChips } from '@/composables/chips'
import { useArchive } from '@/composables/archive'
import { useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'

import ListItem from '@/components/shared/ListItem.vue'
import CompanyDetail from '@/components/shared/CompanyDetail.vue'
import CompanyEdit from '@/components/shared/CompanyEdit.vue'
import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import SignalBar from '@/components/shared/signal-bar/SignalBar.vue'


const { getCompanyChips } = useChips()
const { archivedChip } = useArchive()
const { isMobile } = useDetectDevice()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const route = useRoute()
const selectedItem = ref(null)
const editItem = ref(null)
const listContainer = ref(null)
let listInstance = null
const webSiteStore = useWebSiteStore()

webSiteStore.fabActions = [
  {
    id: 'add',
    icon: 'mdi-plus',
    onClick: () => { editItem.value = {} },
  },
]

const dataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.companiesUrl, {
  searchExpr: 'search_field',
})

const onDetailUpdate = () => {
  selectedItem.value = null
  dataSource.pageIndex(0)
  dataSource.reload()
}

const onEditItemCreated = async () => {
  editItem.value = null
  webSiteStore.activateUpdatedDateSort(route.name)
  dataSource.sort(webSiteStore.buildSortOrderBy(route.name))
  dataSource.pageIndex(0)
  const loaded = await dataSource.reload()
  await highlightRowAsync(loaded?.[0]?.id, listContainer)
  listInstance?.repaint()
}

const onEditItemChanged = () => {
  editItem.value = null
  dataSource.reload()
}


useEntityChanges([entityChangeTypeEnum.COMPANY], (change) => (change.created ? onEditItemCreated() : dataSource.reload()), () => editItem.value !== null)
</script>
<template>
  <div>
    <filter-sort-selector
      :data-source="dataSource"
      :kpi-enabled="true"
      :kpi-visible="webSiteStore.isKpiBarVisible('companies')"
      @toggle-kpi="webSiteStore.toggleKpiBar('companies')"
    />
    <signal-bar page="companies" embedded :visible="webSiteStore.isKpiBarVisible('companies')" />
    <q-card flat>
      <q-card-section ref="listContainer" class="q-pa-none">
        <q-dialog
          maximized
          :model-value="selectedItem !== null"
        >
          <company-detail
            :model-value="selectedItem"
            @cancel="onDetailUpdate"
            @update="onDetailUpdate"
          />
        </q-dialog>
        <q-dialog
          :maximized="isMobile"
          :model-value="editItem !== null"
          class="dialog-sm"
        >
          <company-edit
            v-if="editItem !== null"
            :model-value="editItem"
            @update="onEditItemCreated"
            @cancel="onEditItemChanged"
            @delete="onEditItemChanged"
          />
        </q-dialog>
        <dx-list
          :data-source="dataSource"
          height="calc(100vh - 8rem)"
          page-load-mode="scrollBottom"
          class="events-list"
          item-template="item"
          selection-mode="none"
          :active-state-enabled="false"
          @initialized="e => listInstance = e.component"
        >
          <template #item="{ data: availableItem }">
            <q-item>
              <list-item
                :key="availableItem.id"
                class="q-pa-sm"
                :avatar="availableItem?.avatar_file || availableItem?.avatar_url"
                :label="availableItem.name"
                :caption-chips="[...getCompanyChips(availableItem), availableItem.archived ? archivedChip : null].filter(Boolean)"
                :class="{ 'row-highlight': availableItem.id === highlightedRowId }"
                :disabled="availableItem.archived"
                dense
                clickable
                @click="selectedItem = availableItem"
              />
            </q-item>
          </template>
        </dx-list>
      </q-card-section>
    </q-card>
  </div>
</template>
<style lang="scss"></style>
